import { useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { CardHeader, Stack, Typography } from '@mui/material';

import { Card } from '@occc/ui-design-system';
import { Switches } from '@occc-app/design-system';
import { CampaignComponentFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentFooterActions';
import { CampaignComponentReadOnlyFooterActions } from '@occc-app/routes/CampaignComponents/components/CampaignComponentReadOnlyFooterActions';
import { CanEditFormFields } from '@occc-app/App/CanEditFormFieldsContext';
import { Offer_Presentation_Priority, Offer_Presentation_Type } from '@occc/graphql';
import { FormLayout } from '@occc-app/routes/CampaignComponents/components/FormLayout';
import { useConfig } from '@occc/config';

import {
  MarketingContentFormValues,
  DesignFormProps,
  MarketingContentFormValuesPaths,
} from './types';
import { OfferContentDesignSection } from './OfferContentDesignSection';
import { AdditionalInformationDesignSection } from './AdditionalInformationDesignSection';
import { PhysicalReceiptLabelOverrideSection } from './PhysicalReceiptLabelOverrideSection';
import { PresentationDesignSection } from './PresentationDesignSection';
import { PresentationTypeWatcher } from './presentation-type.watcher';
import { OfferFundingDesignSection } from './OfferFundingDesignSection';

import { OfferForm } from '../components/OfferForm';
import { CustomData } from '../../routes/CampaignComponents/components/CustomData/CustomData';
import { Flow_Types, FormPropsWithNextStepHandler } from '../types';

export const DesignForm = ({
  onSubmit,
  validate,
  initialValues,
  onCancelClick,
  loading,
  currentFlowType,
  showExternalUrl = false,
  showSurgeonGeneralsWarning = true,
  hiddenUntilProgressIsEnabled,
  isFormEditable,
  orderAheadEnabled = false,
  onNextStepButtonClick,
}: FormPropsWithNextStepHandler<DesignFormProps>) => {
  const isCategoryFieldVisible = useConfig('offerEditor').categoryFieldVisible;

  const renderForm: FormRenderProps<MarketingContentFormValues>['render'] = useCallback(
    ({
      handleSubmit,
      submitting,
      values,
      dirty,
      form,
    }: FormRenderProps<MarketingContentFormValues>) => {
      const handleCancelClick = () => {
        onCancelClick(dirty);
      };

      if (!values.marketingContent) {
        // Ensure marketingContent is always present in form values.
        // On the initial render, the object exists as expected. However, if a user fills in
        // any marketingContent field and then clears all values, Final Form automatically
        // removes the entire marketingContent object.
        //
        // Example of such issue: https://stuzo.atlassian.net/browse/OCST-14542
        values.marketingContent = {};
      }

      const formFooterActionButtons = isFormEditable ? (
        <CampaignComponentFooterActions
          currentFlowType={currentFlowType}
          onCancel={handleCancelClick}
          isSubmitDisabled={submitting || (currentFlowType === Flow_Types.Update && !dirty)}
        />
      ) : (
        <CampaignComponentReadOnlyFooterActions
          onCancel={handleCancelClick}
          onNextStepClick={onNextStepButtonClick}
        />
      );

      const isPresentationTypeTile =
        values.offer.presentationType === Offer_Presentation_Type.Default;
      const isPresentationPriorityFeatured =
        values.offer.presentationPriority === Offer_Presentation_Priority.Featured;
      const isPresentationPriorityEnabled = isPresentationTypeTile;
      const isDisplayCategoriesEnabled = isPresentationTypeTile && !isPresentationPriorityFeatured;

      return (
        <OfferForm dirty={!submitting && dirty} handleSubmit={handleSubmit}>
          <FormLayout actions={formFooterActionButtons} type='primary' isLoading={loading}>
            <Stack spacing={1}>
              <Stack p={1.5} spacing={1} component={Card}>
                <OfferContentDesignSection
                  marketingContent={values.marketingContent}
                  showExternalUrl={showExternalUrl}
                />
                <AdditionalInformationDesignSection
                  showSurgeonGeneralsWarning={showSurgeonGeneralsWarning}
                />
                <PresentationDesignSection
                  presentationTypeEnabled
                  displayCategoriesEnabled={isDisplayCategoriesEnabled}
                  presentationPriorityEnabled={isPresentationPriorityEnabled}
                  orderAheadEnabled={orderAheadEnabled}
                  isCategoryFieldVisible={isCategoryFieldVisible}
                />
                <PresentationTypeWatcher form={form} />
                <OfferFundingDesignSection />
                <PhysicalReceiptLabelOverrideSection />
              </Stack>

              {hiddenUntilProgressIsEnabled && (
                <Card variant='outlined'>
                  <CardHeader
                    title={
                      <Typography variant='h6' component='p' gutterBottom>
                        Hidden Until in Progress
                      </Typography>
                    }
                    subheader={
                      <Typography color='textSecondary'>
                        Hide this punch card or challenge from members in Experience until they
                        begin making progress against it
                      </Typography>
                    }
                    action={
                      <Switches
                        name={
                          'earningMechanism.hiddenUntilProgress' satisfies MarketingContentFormValuesPaths
                        }
                        data={{}}
                      />
                    }
                  />
                </Card>
              )}

              <CustomData values={values.customData} label='Marketing Custom Data' />
            </Stack>
          </FormLayout>
        </OfferForm>
      );
    },
    [
      isFormEditable,
      currentFlowType,
      onNextStepButtonClick,
      showExternalUrl,
      showSurgeonGeneralsWarning,
      hiddenUntilProgressIsEnabled,
      onCancelClick,
      orderAheadEnabled,
      loading,
      isCategoryFieldVisible,
    ],
  );

  return (
    <CanEditFormFields isEditable={isFormEditable}>
      <Form<MarketingContentFormValues>
        onSubmit={onSubmit}
        render={renderForm}
        validate={validate}
        initialValues={initialValues}
      />
    </CanEditFormFields>
  );
};
